import {
  useEditContractorsMutation,
  useGetContractorsQuery,
} from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { DataTable, DataTableProps } from 'common/components/DataTable';
import { Contractor } from 'common/models';
import * as notifier from 'common/services/notification';
import {
  ContractorRows,
  useContractorsTableData,
} from 'features/settings-view/hooks/useContractorsTableData';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';

export type ContractorValues = Record<
  string,
  { vendorId: string; active: boolean }
>;

export const ContractorsView: FC = () => {
  const {
    data: contractors,
    isLoading,
    isFetching,
    error,
  } = useGetContractorsQuery();
  const [editContractors, { isLoading: isSubmitting }] =
    useEditContractorsMutation();

  const formatDefaultContractors = (
    contractors: Contractor[],
  ): ContractorValues => {
    return contractors.reduce((acc, curr) => {
      const { id, vendorId, active } = curr;

      acc[id] = { vendorId: vendorId || '', active };

      return acc;
    }, {} as ContractorValues);
  };

  const methods = useForm<ContractorValues>({
    values: formatDefaultContractors(contractors || []),
  });
  const {
    formState: { isDirty },
    register,
    reset,
  } = methods;
  const { columns, data, isEditing, setIsEditing } = useContractorsTableData(
    contractors || [],
    register,
    isSubmitting,
  );

  const closeForm = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit = async (data: ContractorValues): Promise<void> => {
    if (!isDirty) {
      closeForm();
      return;
    }

    const contractorsToEdit = Object.entries(data).map(
      ([id, { vendorId, active }]) => ({
        id: parseInt(id, 10),
        vendorId: vendorId ? vendorId.toUpperCase() : vendorId,
        active,
      }),
    );

    try {
      await editContractors(contractorsToEdit).unwrap();
      notifier.showSuccessMessage('Contractors updated successfully.');
      closeForm();
    } catch (e) {
      handleError(e, 'Unable to edit contractors.');
    }
  };

  const tableProps: DataTableProps<ContractorRows, ContractorValues> = {
    title: 'Contractors',
    columns,
    data,
    isLoading,
    isFetching,
    maxTableWidth: '1000px',
    formProps: {
      methods,
      onSubmit,
      onCancel: closeForm,
      isEditing,
      setIsEditing,
      isDirty,
      isSubmitting,
    },
  };

  useEffect(() => {
    if (error) {
      handleError(error, 'Unable to fetch contractors.');
    }
  }, [error]);

  return <DataTable {...tableProps} />;
};
